import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useDrop } from 'react-dnd';
import IconUploadImage from '../../assets/icons/IconUploadImage';
import CustomTooltip from '../../ui/atoms/CustomTooltip'; // Adjust the import path
import useImages from '../MainComponent/hooks/useImages';
import { RootState, useAppSelector } from '../../redux/Store';
import { useDispatch, useSelector } from 'react-redux';
import ImageDialog from './ImageDialog';
import useMediaLabels from '../MainComponent/hooks/useMediaLabels';
import {
  addMediaLabelToImage,
  moveMediasFromLabel,
  AdditionalLabelValue,
  removeAdditionalLabel,
  setAdditionalLabel,
} from '../../redux/reducer/coreScriptReducer';

import { debounce } from '../../utils';
import IconRemove from '../../assets/icons/IconRemove';
import IconMove from '../../assets/icons/IconMove';
import IconCopy from '../../assets/icons/IconCopy';
import MultiKebab from '../../ui/organisms/MenuItems/MultiKebab';
import { ImageMetadata } from '../../types/ui';
import useMediaHelper from '../MainComponent/hooks/useMediaHelper';

import useQueryObject from '../../hooks/useQueryObject';
import { add } from 'lodash';

type LabelMediaAdditionalItemProps = {
  item: AdditionalLabelValue;
  index: number;
  selected?: boolean;
  expanded?: boolean;
};

const LabelMediaAdditionalItem = ({
  index,
  item,
  expanded,
}: LabelMediaAdditionalItemProps) => {
  const dispatch = useDispatch();

  const { getAllAdditionalImagesForViewing } = useMediaLabels();

  const menuOptions = useLabelMenuOptions(item, () => {
    setIsMenuOpen(false);
  });
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly
  );

  const label = item?.Label?.toLanguagePack();
  const statusColor = '#4CAF50';

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const hasWebFileName = useMemo(() => {
    return item.Images?.[0]?.webFileName || null;
  }, [item]);

  const [imagePreview, setImagesPreview] = useState<boolean>(false);

  const images = useMemo(() => {
    if (!imagePreview) return [];
    return getAllAdditionalImagesForViewing();
  }, [imagePreview, getAllAdditionalImagesForViewing]);

  useEffect(() => {
    if (!hasWebFileName) {
      setIsMenuOpen(false);
    }
  }, [hasWebFileName]);

  useEffect(() => {
    if (!images || (images.length === 0 && imagePreview)) {
      setImagesPreview(false);
    }
  }, [images, imagePreview]);

  const onDropImageLabel = useCallback(
    (draggedObjects: any) => {
      // Support both direct array and { images, indices } object
      const imagesArray: ImageMetadata[] = Array.isArray(draggedObjects)
        ? draggedObjects
        : Array.isArray(draggedObjects?.images)
          ? draggedObjects.images
          : [];

      if (!Array.isArray(imagesArray) || imagesArray.length === 0) return;
      if (!item.Label) {
        return console.error(
          'Guid or Media Label not found in dragged objects or item',
          draggedObjects,
          item
        );
      }

      for (const media of imagesArray) {
        dispatch(setAdditionalLabel({ label: item.Label, image: media }));
      }
    },
    [dispatch, item]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: 'image',
      drop: debounce(onDropImageLabel, 10),
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
      }),
    }),
    [onDropImageLabel, debounce]
  );

  const truncatedLabelName =
    label && label.length > 10 ? `${label.slice(0, 10)}...` : label;

  return (
    <>
      <Box
        ref={drop}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: expanded ? '200px' : '100px',
          padding: 0,
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
          borderRadius: expanded ? '8px' : '10px',
          border: isOver ? '2px dashed #1D6EC9' : '1px solid #D8E0E5',
          background: '#FFF',

          cursor: hasWebFileName ? 'pointer' : 'default',
          overflow: 'hidden',
          transition: 'width 0.3s ease-in-out', // Smooth transition for expanded width
          '@media (max-width: 600px)': {
            // Adjustments for smaller screens
            width: '100%',
            padding: '4px',
          },
        }}
        onDoubleClick={() => {
          if (hasWebFileName) {
            setImagesPreview(true);
          }
        }}
      >
        {/* Image Section */}
        <Box
          sx={{
            width: '100%',
            height: 0,
            paddingTop: '75%', // Fixed aspect ratio (4:3)
            backgroundColor: '#F4F4F4', // Placeholder for no image
            position: 'relative',
          }}
        >
          {hasWebFileName ? (
            <img
              src={hasWebFileName}
              alt={label || ''}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <IconUploadImage />
            </Box>
          )}
        </Box>

        {/* Label and Kebab Menu (Horizontally Aligned) */}
        <Box
          sx={{
            width: '100%',
            padding: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between', // Justifies space between label and kebab menu
            marginTop: '-8px',
            height: '50px',
          }}
        >
          <Typography
            sx={{
              color: '#343330',
              fontFamily: 'Montserrat',
              fontSize: expanded ? '12px' : '10px',
              fontWeight: 500,
              textTransform: 'capitalize',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3, // Limit to 3 lines
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: 1.5,
              textAlign: 'left', // Align the text to the left
              flex: 1, // Ensures the label takes available space
            }}
          >
            <CustomTooltip label={label} placement='top' arrow>
              <span>{label}</span>
            </CustomTooltip>
          </Typography>
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              cursor: 'pointer',
            }}
          >
            <MultiKebab
              disabled={!hasWebFileName || isReadOnly}
              options={menuOptions}
              isMenuOpen={isMenuOpen}
              setIsMenuOpen={(value: boolean) => {
                if (!hasWebFileName) return;
                setIsMenuOpen(value);
              }}
            />
          </Box>
        </Box>

        {/* Status Badge */}
        <Box
          sx={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            backgroundColor: statusColor,
            color: '#FFF',
            padding: '4px 8px',
            borderRadius: '12px',
            fontFamily: 'Montserrat',
            fontSize: '12px',
            fontWeight: 500,
            zIndex: 3,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {item.Images?.length || 0}
        </Box>
      </Box>

      {/* Image Dialog */}
      {images && images.length > 0 && (
        <ImageDialog
          open={imagePreview}
          initialIndex={0}
          images={images}
          onClose={() => setImagesPreview((prev) => !prev)}
          type='addtionallabel'
        />
      )}
    </>
  );
};

const useLabelMenuOptions = (
  item: AdditionalLabelValue,
  hideMenu?: () => void
) => {
  const dispatch = useDispatch();
  const onRemoveLabel = useCallback(() => {
    dispatch(removeAdditionalLabel({ label: item.Label }));
    hideMenu?.();
  }, [item, dispatch, hideMenu]);

  return useMemo(() => {
    return [
      {
        id: '1',
        label: 'Remove Label',
        onClick: onRemoveLabel,
        prefixIcon: <IconRemove />,
      },
    ];
  }, [onRemoveLabel]);
};

export default React.memo(LabelMediaAdditionalItem);
